/**
 * Utilitaires DOM et helpers génériques.
 */

import { getLang } from './i18n.js';

/**
 * Crée un élément HTML avec attributs et enfants.
 *
 * @example
 * el('button', { class: 'btn btn-primary', onclick: () => alert('hi') }, 'Cliquez')
 */
export function el(tag, attrs = {}, ...children) {
  const node = document.createElement(tag);

  for (const [key, value] of Object.entries(attrs)) {
    if (value == null || value === false) continue;

    if (key.startsWith('on') && typeof value === 'function') {
      node.addEventListener(key.slice(2).toLowerCase(), value);
    } else if (key === 'class') {
      node.className = value;
    } else if (key === 'dataset') {
      Object.assign(node.dataset, value);
    } else if (key === 'style' && typeof value === 'object') {
      Object.assign(node.style, value);
    } else {
      node.setAttribute(key, value);
    }
  }

  for (const child of children.flat()) {
    if (child == null || child === false) continue;
    node.append(child instanceof Node ? child : String(child));
  }

  return node;
}

/**
 * Vide un élément (plus rapide que innerHTML = '').
 */
export function clear(node) {
  while (node.firstChild) node.removeChild(node.firstChild);
}

/**
 * Format de date court, localisé selon la langue courante.
 * FR : "14 mars 2026" — EN : "14 March 2026"
 */
export function formatDate(date) {
  const d = typeof date === 'string' ? new Date(date) : date;
  const locale = getLang() === 'en' ? 'en-GB' : 'fr-FR';
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

/**
 * Retarde l'exécution d'une fonction (debounce simple).
 */
export function debounce(fn, delay = 250) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}
